import { useForm } from "react-hook-form";
import { Stack } from "@mui/material";
import dayjs from "dayjs";
import { z } from "zod";
import { useTranslation } from "@privacy/translations";
import { DatePicker } from "../shared/DatePicker";
import * as FormDialog from "../shared/FormDialog";
import { useTrustUserInfo } from "./TrustUserInfoProvider";
import { useMutation } from "@tanstack/react-query";
import { signAssessmentMutationOptions } from "./trustAPI";

const EmailSchema = z.email();

type TrustSignDialogFormValues = { firstName: string; lastName: string; jobTitle: string; email: string };

type TrustSignDialogProps = {
  onClose: () => void;
  onSigned: () => void;
  assessmentId: string;
};

export const TrustSignDialog = ({ onClose, onSigned, assessmentId }: TrustSignDialogProps) => {
  const { t } = useTranslation();
  const userInfo = useTrustUserInfo();
  const signAssessmentMutation = useMutation(signAssessmentMutationOptions(assessmentId));

  const { control, handleSubmit } = useForm<TrustSignDialogFormValues>({
    defaultValues: {
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      jobTitle: userInfo.jobTitle,
      email: userInfo.email,
    },
  });

  return (
    <FormDialog.Dialog isOpen maxWidth="720px">
      <FormDialog.Form
        onSubmit={handleSubmit(values => signAssessmentMutation.mutate(values, { onSuccess: onSigned }))}
      >
        <FormDialog.Title>{t("Trust.SignDialog.Title")}</FormDialog.Title>
        <FormDialog.Content>
          <FormDialog.FormError isError={signAssessmentMutation.isError} />
          <FormDialog.Fields>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormDialog.TextField
                control={control}
                name="firstName"
                rules={{ required: t("Input.Validation.Required") }}
                label={t("Trust.UserInfoDialog.FirstNameLabel")}
              />
              <FormDialog.TextField
                control={control}
                name="lastName"
                rules={{ required: t("Input.Validation.Required") }}
                label={t("Trust.UserInfoDialog.LastNameLabel")}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormDialog.TextField
                control={control}
                name="email"
                rules={{
                  validate: value => {
                    if (!value.trim()) return t("Input.Validation.Required");
                    return EmailSchema.safeParse(value).success || t("Input.Validation.RequiredEmail");
                  },
                }}
                label={t("Trust.SignDialog.EmailLabel")}
              />
              <FormDialog.TextField
                control={control}
                name="jobTitle"
                rules={{ required: t("Input.Validation.Required") }}
                label={t("Trust.SignDialog.TitleLabel")}
                placeholder={t("Trust.SignDialog.TitlePlaceholder")}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormDialog.Field>
                <FormDialog.Label htmlFor="trust-sign-date">{t("Trust.SignDialog.DateLabel")}</FormDialog.Label>
                <DatePicker
                  disabled
                  format="DD MMMM YYYY"
                  value={dayjs()}
                  slotProps={{ textField: { id: "trust-sign-date", size: "small", fullWidth: true } }}
                />
              </FormDialog.Field>
            </Stack>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton
            onClick={() => {
              if (signAssessmentMutation.isPending) return;
              onClose();
            }}
          />
          <FormDialog.SubmitButton isSubmitting={signAssessmentMutation.isPending}>
            {t("Trust.Card.SignAndSend")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
